<!-- ============================================================
     ELIGIBILITY CHECK PAGE
     ============================================================ -->
<div class="dashboard-card">

  <!-- Page Header -->
  <div class="page-header mb-4">
    <div>
      <h1 class="page-title">
        <i class="bi bi-search-heart-fill me-2 text-primary" style="font-size: 1.2rem;"></i>
        Eligibility Check
      </h1>
      <p class="page-subtitle">
        Verify a student's eligibility against configured admission rules instantly
      </p>
    </div>
    <div class="d-flex gap-2">
      <a routerLink="/dashboard/{{dataService.myEncrypt('slg')}}" class="btn btn-outline-primary btn-sm d-flex align-items-center gap-2">
        <i class="bi bi-sliders2-vertical"></i> Manage Rules
      </a>
      <!-- @if (checkDone()) {
        <button class="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2" (click)="resetCheck()">
          <i class="bi bi-arrow-counterclockwise"></i> New Check
        </button>
        <button class="btn btn-primary-erp btn-sm" (click)="printResult()">
          <i class="bi bi-printer-fill me-1"></i> Print Result
        </button>
      } -->
    </div>
  </div>

  <div class="row g-4">

    <!-- ===================== LEFT: INPUT FORM ===================== -->
    <div class="col-xl-6">

      <!-- Rule Selector -->
      <div class="ec-section-card mb-3">
        <div class="ec-section-header">
          <i class="bi bi-journal-check"></i> Select Eligibility Rule
        </div>
        <div class="ec-section-body">
          <div class="row g-3">
            <div class="col-12">
              <label class="form-label">Program / Class Rule <span class="required-star">*</span></label>
              <select
                class="form-select form-select"
                [(ngModel)]="selectedRuleId"
                [class.is-invalid]="submitted() && !selectedRuleId"
              >
                <option value="">-- Select a rule to check against --</option>
                @for (rule of activeRules; track rule.id) {
                  <option [value]="rule.id">
                    {{ rule.ruleName }} ({{ rule.status }})
                  </option>
                }
              </select>
              @if (submitted() && !selectedRuleId) {
                <div class="invalid-feedback">Please select a rule.</div>
              }
            </div>
          </div>

          <!-- Rule summary pill row -->
          @if (selectedRule) {
            <div class="rule-summary-pills mt-3">
              <div class="rsp-item">
                <i class="bi bi-book text-primary"></i>
                {{ dataService.getClassName(selectedRule.programClass) }}
              </div>
              <div class="rsp-item">
                <i class="bi bi-percent text-success"></i>
                Min {{ selectedRule.minPercentage }}%
              </div>
              @if (selectedRule.minAge) {
                <div class="rsp-item">
                  <i class="bi bi-calendar-person text-warning"></i>
                  Age {{ selectedRule.minAge }}–{{ selectedRule.maxAge }} yrs
                </div>
              }
              @if (selectedRule.entranceRequired) {
                <div class="rsp-item">
                  <i class="bi bi-pencil text-purple"></i>
                  Entrance required
                </div>
              }
              <div class="rsp-item">
                <i class="bi bi-people text-info"></i>
                {{ selectedRule.categorySeats.reduce(toTotalSeats, 0) }} seats
              </div>
            </div>
          }
        </div>
      </div>

      <!-- Student Details -->
      <div class="ec-section-card mb-3">
        <div class="ec-section-header">
          <i class="bi bi-person-fill"></i> Student Details
        </div>
        <div class="ec-section-body">
          <div class="row g-3 mb-2">
            <div class="col-8">
              <div class="input-group">
                <input type="text" class="form-control py-2" placeholder="Search Application" 
                aria-label="Institute ID" aria-describedby="button-addon2" [(ngModel)]="searchTerm"
                (keyup.enter)="onSearchChange()">
                <button class="btn btn-primary" type="button" id="button-addon2" (click)="onSearchChange()">Search</button>
              </div>
            </div>
          </div>

          <div class="row g-3">
            <div class="col-md-7">
              <label class="form-label">Student Full Name <span class="required-star">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="student.name"
                placeholder="Full name as per records" maxlength="100"
                [class.is-invalid]="submitted() && !student.name"
                [class.is-valid]="student.name && submitted()">
              @if (submitted() && !student.name) {
                <div class="invalid-feedback">Name is required.</div>
              }
            </div>
            <div class="col-md-5">
              <label class="form-label">Gender</label>
              <select class="form-select" [(ngModel)]="student.gender">
                <option value="">-- Select --</option>
                @for (g of genderOptions; track g) {
                  <option [value]="g">{{ g }}</option>
                }
              </select>
            </div>
            <div class="col-md-5">
              <label class="form-label">Date of Birth <span class="required-star">*</span></label>
              <input type="date" class="form-control" [(ngModel)]="student.dateOfBirth"
                [class.is-invalid]="submitted() && !student.dateOfBirth"
                [class.is-valid]="student.dateOfBirth && submitted()">
              @if (submitted() && !student.dateOfBirth) {
                <div class="invalid-feedback">Date of birth is required.</div>
              }
              @if (student.dateOfBirth && selectedRule?.ageAsOnDate) {
                <div class="form-text text-primary fw-semibold">
                  Age as on {{selectedRule?.ageAsOnDate}}: 
                  <strong>{{ computeAge(student.dateOfBirth, selectedRule!.ageAsOnDate) }} years</strong>
                </div>
              }
            </div>
            <div class="col-md-7">
              <label class="form-label">Category <span class="required-star">*</span></label>
              <select class="form-select" [(ngModel)]="student.category"
                [class.is-invalid]="submitted() && !student.category">
                <option value="">-- Select Category --</option>
                @for (c of allCategories; track c) {
                  <option [value]="c.code">{{ c.code }} - {{ c.name }}</option>
                }
              </select>
              @if (submitted() && !student.category) {
                <div class="invalid-feedback">Category is required.</div>
              }
            </div>
            <div class="col-md-6">
              <label class="form-label">Aadhaar Number</label>
              <input type="text" class="form-control" [(ngModel)]="student.aadhaarNumber"
                pattern="^\d{12}$" maxlength="12" placeholder="12-digit Aadhaar">
            </div>
            <div class="col-md-6">
              <label class="form-label">Nationality</label>
              <input type="text" class="form-control" [(ngModel)]="student.nationality" maxlength="50">
            </div>
          </div>
        </div>
      </div>

      <!-- Academic Details -->
      <div class="ec-section-card mb-3">
        <div class="ec-section-header">
          <i class="bi bi-journal-bookmark-fill"></i> Academic Details
        </div>
        <div class="ec-section-body">
          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="form-label">Previous School <span class="required-star">*</span></label>
              <input type="text" class="form-control" [(ngModel)]="student.previousSchool"
                placeholder="Name of previous institution" maxlength="200">
            </div>
            <div class="col-md-6">
              <label class="form-label">Board <span class="required-star">*</span></label>
              <select class="form-select" [(ngModel)]="student.board"
                [class.is-invalid]="submitted() && !student.board">
                <option value="">-- Select Board --</option>
                @for (b of boardOptions; track b) {
                  <option [value]="b.code">{{ b.code }} - {{ b.name }}</option>
                }
              </select>
              @if (submitted() && !student.board) {
                <div class="invalid-feedback">Board is required.</div>
              }
            </div>
            <div class="col-md-5">
              <label class="form-label">Passing Year <span class="required-star">*</span></label>
              <select class="form-select" [(ngModel)]="student.passingYear"
                [class.is-invalid]="submitted() && !student.passingYear">
                <option value="">-- Year --</option>
                @for (y of passingYears; track y) {
                  <option [value]="y">{{ y }}</option>
                }
              </select>
              @if (submitted() && !student.passingYear) {
                <div class="invalid-feedback">Passing year is required.</div>
              }
            </div>
            <div class="col-md-7">
              <label class="form-label">Overall Percentage / CGPA <span class="required-star">*</span></label>
              <div class="input-group">
                <input type="number" class="form-control" [(ngModel)]="student.percentage"
                  min="0" max="100" step="0.1" placeholder="e.g. 78.5"
                  [class.is-invalid]="submitted() && student.percentage === null"
                  [class.is-valid]="student.percentage !== null && submitted()">
                <span class="input-group-text">%</span>
              </div>
              @if (submitted() && student.percentage === null) {
                <div class="invalid-feedback d-block">Percentage is required.</div>
              }
            </div>
          </div>

          <!-- Subject marks -->
          <p class="sub-label-ec"><i class="bi bi-list-ol me-2"></i>Subject-wise Marks</p>
          @for (sub of subjects; track sub; let i = $index) {
            <div class="subject-row-ec">
              <div class="row g-2 align-items-center">
                <div class="col-auto text-muted small fw-semibold" style="min-width: 26px;">{{ i + 1 }}.</div>
                <div class="col-md">
                  <input type="text" class="form-control form-control-sm"
                    [(ngModel)]="sub.name" [name]="'subName' + i"
                    placeholder="Subject (e.g. Mathematics)">
                </div>
                <div class="col-md-4">
                  <div class="input-group input-group-sm">
                    <input type="number" class="form-control" [(ngModel)]="sub.marksObtained"
                      [name]="'marks' + i" min="0" [max]="sub.maxMarks ?? 100" placeholder="Obtained">
                    <span class="input-group-text">/</span>
                    <input type="number" class="form-control" [(ngModel)]="sub.maxMarks"
                      [name]="'max' + i" min="1" max="1000" placeholder="Max" style="max-width: 64px;">
                  </div>
                </div>
                <div class="col-auto">
                  <button type="button" class="btn-remove-sm-ec" (click)="removeSubject(i)"
                    [disabled]="subjects.length === 1">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>
          }
          <button type="button" class="btn btn-add-row-ec mt-2" (click)="addSubject()">
            <i class="bi bi-plus-circle me-1"></i> Add Subject
          </button>
        </div>
      </div>

      <!-- Conditional: Entrance / Domicile / Medical -->
      @if (selectedRule?.entranceRequired || selectedRule?.domicileRequired || selectedRule?.medicalFitness) {
        <div class="ec-section-card mb-3">
          <div class="ec-section-header">
            <i class="bi bi-clipboard2-check-fill"></i> Additional Requirements
          </div>
          <div class="ec-section-body">
            <div class="row g-3">
              @if (selectedRule?.entranceRequired) {
                <div class="col-md-6">
                  <label class="form-label">
                    {{ selectedRule!.entranceExamName || 'Entrance Exam' }} Score
                    <span class="required-star">*</span>
                  </label>
                  <input type="number" class="form-control" [(ngModel)]="student.entranceScore"
                    min="0" max="100" placeholder="e.g. 72">
                  <div class="form-text">Minimum required: {{ selectedRule!.minEntranceScore }}</div>
                </div>
              }
              @if (selectedRule?.domicileRequired) {
                <div class="col-md-6">
                  <label class="form-label d-block">Domicile Certificate</label>
                  <div class="form-check form-switch mt-2">
                    <input class="form-check-input" type="checkbox" id="hasDomicile"
                      [(ngModel)]="student.hasDomicile">
                    <label class="form-check-label" for="hasDomicile">Certificate available</label>
                  </div>
                </div>
              }
              @if (selectedRule?.medicalFitness) {
                <div class="col-md-6">
                  <label class="form-label d-block">Medical Fitness Certificate</label>
                  <div class="form-check form-switch mt-2">
                    <input class="form-check-input" type="checkbox" id="hasMedical"
                      [(ngModel)]="student.hasMedicalFitness">
                    <label class="form-check-label" for="hasMedical">Certificate available</label>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- Check Button -->
      <div class="check-action-bar">
        <!-- <button type="button" class="btn btn-check-eligibility" (click)="runCheck()"> -->
        <button type="button" class="btn btn-primary" (click)="runCheck()">
          <i class="bi bi-search-heart me-2"></i>
          Check Eligibility
        </button>
        <button type="button" class="btn btn-outline-secondary ms-3" (click)="resetCheck()">
          <i class="bi bi-arrow-counterclockwise me-2"></i> Reset
        </button>
      </div>

      @if (submitted() && (!selectedRuleId || !student.name || !student.dateOfBirth || !student.category || !student.board || !student.passingYear || student.percentage === null)) {
        <div class="alert alert-danger d-flex align-items-center gap-2 mt-3" role="alert">
          <i class="bi bi-exclamation-triangle-fill"></i>
          Please fill all required fields to run the eligibility check.
        </div>
      }

    </div>

    <!-- ===================== RIGHT: RESULT ===================== -->
    <div class="col-xl-6" id="check-result">

      @if (!checkDone()) {
        <!-- Placeholder state -->
        <div class="result-placeholder">
          <div class="rp-icon">
            <i class="bi bi-search-heart"></i>
          </div>
          <h5>Fill Student Details</h5>
          <p>Select an eligibility rule and enter the student's information on the left, then click <strong>Check Eligibility</strong> to see a detailed result here.</p>
          <div class="rp-steps">
            <div class="rp-step">
              <span class="step-num">1</span>
              <span>Select admission rule</span>
            </div>
            <div class="rp-step">
              <span class="step-num">2</span>
              <span>Enter student details</span>
            </div>
            <div class="rp-step">
              <span class="step-num">3</span>
              <span>Click "Check Eligibility"</span>
            </div>
            <div class="rp-step">
              <span class="step-num">4</span>
              <span>View detailed result</span>
            </div>
          </div>
        </div>

      } @else {
        <!-- RESULT CARD -->
        @let result = checkResult()!;

        <!-- Verdict Banner -->
        <div class="verdict-banner" [class.verdict-eligible]="result.eligible" [class.verdict-ineligible]="!result.eligible">
          <div class="verdict-icon">
            <i class="bi" [class.bi-check-circle-fill]="result.eligible" [class.bi-x-circle-fill]="!result.eligible"></i>
          </div>
          <div class="verdict-text">
            <h3>{{ result.eligible ? 'ELIGIBLE' : 'NOT ELIGIBLE' }}</h3>
            <p>{{ student.name }} &nbsp;·&nbsp; {{ dataService.getClassName(result.programClass) }} Class</p>
            <p class="small opacity-75">Rule: {{ result.ruleName }}</p>
          </div>
          <div class="verdict-score">
            <div class="vs-circle" [class.vs-pass]="result.eligible" [class.vs-fail]="!result.eligible">
              <span class="vs-num">{{ result.passed }}</span>
              <span class="vs-den">/{{ result.totalCriteria }}</span>
            </div>
            <div class="vs-label">Criteria Passed</div>
          </div>
        </div>

        <!-- Criteria Summary Row -->
        <div class="criteria-summary">
          <div class="cs-item cs-pass">
            <i class="bi bi-check-circle-fill"></i>
            <span class="cs-num">{{ result.passed }}</span>
            <span class="cs-label">Passed</span>
          </div>
          <div class="cs-item cs-fail">
            <i class="bi bi-x-circle-fill"></i>
            <span class="cs-num">{{ result.failed }}</span>
            <span class="cs-label">Failed</span>
          </div>
          <div class="cs-item cs-warn">
            <i class="bi bi-exclamation-triangle-fill"></i>
            <span class="cs-num">{{ result.warned }}</span>
            <span class="cs-label">Warnings</span>
          </div>
          <div class="cs-item cs-total">
            <i class="bi bi-list-check"></i>
            <span class="cs-num">{{ result.totalCriteria }}</span>
            <span class="cs-label">Total</span>
          </div>
        </div>

        <!-- Criteria Detail Cards -->
        <div class="criteria-list mt-3">
          @for (c of result.criteria; track c.label) {
            <div class="criterion-card" [class.crit-pass]="c.status === 'pass'"
              [class.crit-fail]="c.status === 'fail'" [class.crit-warn]="c.status === 'warn'">
              <div class="crit-icon-wrap">
                <i class="bi {{ c.icon }}" [style.color]="getStatusColor(c.status)"></i>
              </div>
              <div class="crit-body">
                <div class="crit-header-row">
                  <span class="crit-label">{{ c.label }}</span>
                  <span class="crit-status-badge status-{{ c.status }}">
                    <i class="bi {{ getStatusIcon(c.status) }} me-1"></i>
                    {{ c.status | titlecase }}
                  </span>
                </div>
                <p class="crit-detail">{{ c.detail }}</p>
                <div class="crit-values">
                  <div class="cv-item">
                    <span class="cv-label">Applicant</span>
                    <span class="cv-value">{{ c.applicantValue }}</span>
                  </div>
                  <div class="cv-sep">→</div>
                  <div class="cv-item">
                    <span class="cv-label">Required</span>
                    <span class="cv-value">{{ c.requiredValue }}</span>
                  </div>
                </div>
              </div>
            </div>
          }
        </div>

        <!-- Document Checklist -->
        @if (selectedRule && selectedRule.documents.length > 0) {
          <div class="ec-section-card mt-3">
            <div class="ec-section-header">
              <i class="bi bi-file-earmark-check-fill"></i> Document Checklist
            </div>
            <div class="ec-section-body p-0">
              <ul class="doc-checklist">
                @for (doc of selectedRule.documents; track doc.name) {
                  <li class="doc-check-item">
                    <i class="bi"
                      [class.bi-check-circle-fill]="!doc.mandatory"
                      [class.bi-exclamation-circle-fill]="doc.mandatory"
                      [style.color]="doc.mandatory ? '#f59e0b' : '#22c55e'"></i>
                    <span class="doc-name">{{ doc.name }}</span>
                    <span class="doc-req-badge" [class.req-mandatory]="doc.mandatory" [class.req-optional]="!doc.mandatory">
                      {{ doc.mandatory ? 'Mandatory' : 'Optional' }}
                    </span>
                  </li>
                }
              </ul>
            </div>
          </div>
        }

        <!-- Remarks -->
        @if (selectedRule?.remarks) {
          <div class="remarks-box mt-3">
            <i class="bi bi-info-circle-fill me-2 text-primary"></i>
            <strong>Remark: </strong> {{ selectedRule!.remarks }}
          </div>
        }

        <!-- Action Buttons -->
        <div class="result-actions my-3 justify-content-center">
          <!-- @if (result.eligible) { -->
            <button class="btn btn-primary-erp" (click)="updateStatus()">
              <i class="bi bi-person-plus-fill me-2"></i> Update Eligibility Status
            </button>
          <!-- }  -->
          <!-- @else {
            <button type="button" class="btn btn-outline-secondary" (click)="resetCheck()">
              <i class="bi bi-arrow-counterclockwise me-2"></i> Try Another Student
            </button>
          } -->
          <button type="button" class="btn btn-outline-primary ms-2" (click)="printResult()">
            <i class="bi bi-printer me-2"></i> Print Result
          </button>
        </div>

      }
    </div>

  </div>
</div>
